import { type PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { CxFooter, HeaderV2 } from '@cx-web/components/composites';
import Logo from '@cx-web/components/primitives/Logo';
import { logout } from '@cx-web/feature-auth';
import { CookieService } from '@cx-web/feature-cookies';
import { getDefaultState, getStateName, isValidState, type StateCode } from '@cx-web/feature-geolocation';
import { changeOddsFormat } from '@cx-web/feature-odds-format';
import { TRACKING_ACTIONS, TRACKING_CATEGORIES, TRACKING_EVENTS, TRACKING_LABELS } from '@cx-web/feature-tracking';
import { getRepubHomeUrl } from '@cx-web/utils';
import { trackEvent } from '@cx-web/utils/dataLayer';
import { useLoaderData, useLocation } from '@remix-run/react';

import MiniBetslipWithContextAndStoreSlices from '~/components/composites/betslip/components/mini-betslip';
import GoogleAdUnit from '~/components/composites/GooglePublisherTag/GooglePublisherTag';
import MiniMyOC from '~/components/composites/MiniMyOC';
import { ONE_DAY } from '~/constants';
import { useGeolocation } from '~/providers/geolocation-context';
import { useLayoutStore } from '~/providers/layout-store-provider';
import { useMyOCAuth } from '~/providers/myoc-auth';
import { useNavigation } from '~/providers/navigation-context';
import { useServicesContext } from '~/providers/services-context';
import MobileBottomNav from '~/routes/$/components/MobileBottomNav';
import { type OddsFormat } from '~/types';
import { type AdUnitConfiguration } from '~/utils/adUnitSetup';
import { useCmpProps } from '~/utils/cmp';

export const LayoutComponents = ({
  adUnitConfiguration,
  children,
  userOddsFormat,
}: PropsWithChildren<{ adUnitConfiguration: AdUnitConfiguration[]; userOddsFormat: OddsFormat }>) => {
  const { myocData } = useMyOCAuth();
  const { currentPath } = useNavigation();
  const { state } = useGeolocation();
  const { labelActions, repub, siteConfig, trackingActions } = useServicesContext();
  const loaderData = useLoaderData<{ url: string }>();
  const { pathname } = useLocation();
  const footerData = useLayoutStore(s => s.footerData);
  const isAdmiralCookieBannerEnabled = useLayoutStore(s => s.isAdmiralCookieBannerEnabled);
  const navData = useLayoutStore(s => s.navData);
  const resourcesUrl = useLayoutStore(s => s.resourcesUrl);
  const cmpProps = useCmpProps();

  const cookieService = useMemo(() => new CookieService(), []);

  const stateOptions = useMemo(() => {
    const flattenedCodes = Object.values(siteConfig?.supportedStates ?? {}).flat();
    return flattenedCodes
      .map(code => ({ code, label: getStateName(code as StateCode) ?? code }))
      .sort((a, b) => (a.label > b.label ? 1 : -1));
  }, [siteConfig?.supportedStates]);

  const selectedState = useMemo(() => {
    const stateCode = state as StateCode;
    const flattenedCodes = Object.values(siteConfig?.supportedStates ?? {}).flat();
    if (isValidState(stateCode) && flattenedCodes.includes(stateCode)) return stateCode;
    return siteConfig?.statesAssignments?.[stateCode] ?? getDefaultState(stateCode) ?? '';
  }, [state, siteConfig?.supportedStates, siteConfig?.statesAssignments]);

  // Optimistic login state: show the logged-in header immediately after the
  // myoc widget reports success, without waiting for a page reload to refresh
  // myocData from the server.
  const [isOCLoggedIn, setIsOCLoggedIn] = useState(!!myocData?.myOCUser);
  useEffect(() => {
    // Tracks whether login succeeded during this modal session. Used by
    // onModalClosed to distinguish "user dismissed without logging in" (should
    // revert optimistic state) from "modal closed after a successful login"
    // (should keep optimistic state, since myocData.myOCUser is still null
    // until the next page load).
    let loginSucceeded = false;
    const onLogin = () => {
      loginSucceeded = true;
      setIsOCLoggedIn(true);
    };
    const onModalClosed = () => {
      // Only revert to logged-out if the user closed without logging in.
      // If login succeeded we keep isOCLoggedIn=true until the next navigation
      // reloads myocData from the server with the real session.
      if (!loginSucceeded && !myocData?.myOCUser) setIsOCLoggedIn(false);
    };
    window.addEventListener('OC:LoggedIn', onLogin);
    window.addEventListener('OC:ModalClosed', onModalClosed);
    return () => {
      window.removeEventListener('OC:LoggedIn', onLogin);
      window.removeEventListener('OC:ModalClosed', onModalClosed);
    };
  }, [myocData?.myOCUser]);

  return (
    <>
      <GoogleAdUnit adUnitConfiguration={adUnitConfiguration} />

      <HeaderV2
        betslipSlot={
          <MiniBetslipWithContextAndStoreSlices betslipTitle={labelActions?.get('RESPONSIVE_BETSLIP') ?? ''} />
        }
        navData={navData}
        navigationKey={pathname}
        oddsFormat={userOddsFormat}
        onMenuToggle={() => {
          trackingActions.eventTracking({
            event: TRACKING_EVENTS.HAMBURGER_MENU_CLICK,
            eventCategory: TRACKING_CATEGORIES.NAVIGATION,
            eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.HAMBURGER_MENU_CLICK],
            eventLabel: TRACKING_LABELS[TRACKING_EVENTS.HAMBURGER_MENU_CLICK],
          });
        }}
        onNavItemClick={({ label }) => {
          trackingActions.eventTracking({
            event: TRACKING_EVENTS.HAMBURGER_MENU_ITEM,
            eventCategory: TRACKING_CATEGORIES.NAVIGATION,
            eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.HAMBURGER_MENU_ITEM],
            eventLabel: label,
          });
        }}
        onOddsFormatChange={changeOddsFormat}
        onStateChange={newState => {
          cookieService.select('Subdivision-Code').set(newState, new Date().getTime() + ONE_DAY);
          cookieService.select('us_state').set(newState, new Date().getTime() + ONE_DAY);
          window.location.reload();
        }}
        renderAccountMenu={variant => (
          <MiniMyOC
            currentPath={currentPath}
            isLoggedIn={isOCLoggedIn}
            labelActions={labelActions}
            myocData={myocData}
            onLogout={() => {
              trackEvent(TRACKING_EVENTS.LOG_OUT, {
                eventCategory: TRACKING_CATEGORIES.ODDSCHECKER_ACCOUNT,
                eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.LOG_OUT],
              });
              logout(repub!, getRepubHomeUrl(repub!));
            }}
            showProfileLink={repub === 'US'}
            siteConfig={siteConfig}
            type={variant === 'desktop' ? 'desktop' : 'burgerTop'}
          />
        )}
        renderLogo={<Logo resourcesUrl={resourcesUrl} />}
        repubHomeUrl={getRepubHomeUrl(repub!)}
        selectedState={selectedState}
        serverUrl={loaderData.url}
        siteConfig={siteConfig}
        stateOptions={stateOptions}
      />
      {children}
      <CxFooter
        cmpProps={cmpProps}
        footerData={footerData}
        isAdmiralCookieBannerEnabled={isAdmiralCookieBannerEnabled}
        resourcesUrl={resourcesUrl}
      />
      <MobileBottomNav />
    </>
  );
};
